import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { NoPaymentInitializedPayload, paymentInitializationFailed, paymentInitializationRetryStarted } from '../../../../../redux/slice/bookingCreation/bookingCreationSlice';
import LoaderOverlay from '../../../../layout/components/LoaderOverlay';
import { useInitializeNoPayment } from '../../../../../redux/flows/initializeNoPaymentFlow';
import { buildAppUrl } from '../../../../../util';
import routes from '../../../../../routes/routes';
import InitializationErrorInfo from './InitializationErrorInfo';
import { RootState } from '../../../../../redux/store';

/**
 * Confirms a booking with nothing to pay and leaves for the return page.
 *
 * It renders exactly two things: the loader while the confirmation runs (and
 * until the browser has left), or the failure panel. It used to start with
 * `isInitialized = true` and render an empty `<section>` — and every parent
 * deliberately skips its own failure panel for `no_payment`, so a failed
 * confirmation (or one started without a reservation, e.g. after a reload of
 * the payment step) left a white screen with nothing to press. The failure
 * panel therefore lives HERE, the one place all eight payment parents share.
 */
const NoPayment = ({ service }: { service: string }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const initializeNoPayment = useInitializeNoPayment({service})
    const [failed, setFailed] = useState(false);
    // Without a reservation in memory (e.g. after a reload) the confirmation
    // cannot succeed — `initializeNoPayment` returns nothing — so a retry
    // would only fail again. Offer "back" alone then.
    const hasReservation = useSelector(
        (state: RootState) => state.bookingCreation.reservations.length > 0
    );
    const paymentInitilaized = useRef(false)

    const manageNoPayment = useCallback(async () => {
        try {
            const response = ((await initializeNoPayment()) as NoPaymentInitializedPayload[] | undefined)?.[0];

            if (!response?.orderIds?.length || !response?.reservationIds?.length) {
                dispatch(paymentInitializationFailed({}));
                setFailed(true);
                return;
            }

            const returnUrl = buildAppUrl(
                `/payments/no_payment/return?reservationIds=${response.reservationIds.join(
                    ","
                )}`
            );

            window.location.replace(returnUrl)
        }
        catch {
            dispatch(paymentInitializationFailed({}));
            setFailed(true);
        }
    }, [dispatch, initializeNoPayment]);

    useEffect(() => {
        // In React Strict Mode, this function is called twice during development.
        // To prevent the payment initialization from running multiple times, we use a ref.
        // In production, this function will only be called once, so using a ref will not cause any issues.
        if (!paymentInitilaized.current) {
            paymentInitilaized.current = true;
            manageNoPayment();
        }
    }, [manageNoPayment]);

    const retry = () => {
        setFailed(false);
        dispatch(paymentInitializationRetryStarted({}));
        manageNoPayment();
    };

    if (failed) {
        return (
            <InitializationErrorInfo
                showTryAgain={hasReservation}
                onBackClick={() => navigate(routes.home)}
                onTryAgainClick={retry}
            />
        );
    }

    return (
        <div className="h-full box-border pt-4">
            <LoaderOverlay open={true} />
        </div>
    );
};

export default NoPayment;
